export const BOTS_SYSTEM = 'http://polarissleep.com/bots';

export {
  CONSENT_DEFINITIONS,
  CONSENT_PENDING_TAG,
  type ConsentDefinition,
  consentCategoryMapping,
  consentPolicyRuleMapping,
  consentScopeMapping,
} from '@polarissleep/shared';

// Polaris-local code system used for v2 gender-identity codes (no widely-accepted
// SNOMED concept exists for Cisgender; ONC's LL3322-6 omits it). Used on the
// intake forms; profile-edit UIs see this list as a UNION with legacy options so
// pre-v2 patients still resolve in the dropdown.
export const POLARIS_CODES_SYSTEM = 'http://polarissleep.com/codes';

// Settings/profile-page dropdown list. Combines v2 options (top — what new
// intake submissions write) with legacy options (bottom — what pre-v2 patients
// still carry on Patient.gender / the birth-sex extension), so editing a legacy
// patient's profile still resolves their current value in the dropdown without
// silently clearing Patient.gender on save.
export const SEX_AT_BIRTH_OPTIONS = [
  // v2 (SLE-243) intake form set:
  { value: 'M', label: 'Male', system: 'http://terminology.hl7.org/CodeSystem/v3-AdministrativeGender' },
  { value: 'F', label: 'Female', system: 'http://terminology.hl7.org/CodeSystem/v3-AdministrativeGender' },
  { value: '32570681000036106', label: 'Intersex', system: 'http://snomed.info/sct' },
  // Legacy options — kept for pre-v2 patients whose existing value would otherwise
  // not match anything in the dropdown.
  { value: 'ASKU', label: 'Asked but unknown', system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor' },
  { value: 'OTH', label: 'Other', system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor' },
  { value: 'UNK', label: 'Unknown', system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor' },
  {
    value: 'asked-declined',
    label: 'Asked but declined',
    system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
  },
];

// Settings/profile-page dropdown for gender identity. Combines v2 options (top —
// what new intake submissions write into Patient.gender) with legacy options
// (bottom — what pre-v2 patients carry). Both code sets resolve in the dropdown.
//
// `value` = the code that gets stored. `fhirGender` is the value setGenderIdentity
// stamps onto Patient.gender at save time; for v2 entries `fhirGender` is the
// code itself (round-trips cleanly so editing a v2 patient's profile doesn't
// clear or mangle the field), for legacy entries it's the historical mapping.
export const GENDER_IDENTITY_OPTIONS = [
  // v2 (SLE-243) intake form set:
  {
    value: 'cisgender',
    label: 'Cisgender',
    display: 'Cisgender',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'cisgender' as const,
  },
  {
    value: 'transgender',
    label: 'Transgender',
    display: 'Transgender',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'transgender' as const,
  },
  {
    value: 'nonbinary',
    label: 'Nonbinary',
    display: 'Nonbinary',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'nonbinary' as const,
  },
  {
    value: 'prefer-not-to-say',
    label: 'Prefer not to say',
    display: 'Prefer not to say',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'prefer-not-to-say' as const,
  },
  {
    value: 'other',
    label: 'Other',
    display: 'Other',
    system: POLARIS_CODES_SYSTEM,
    fhirGender: 'other' as const,
  },
  // Legacy options — pre-v2 patients still carry these on Patient.gender / the
  // gender-identity extension. Kept so profile edits don't clear their data.
  {
    value: '446141000124107',
    label: 'Identifies as female gender',
    display: 'Identifies as female gender (finding)',
    system: 'http://snomed.info/sct',
    fhirGender: 'female' as const,
  },
  {
    value: '446151000124109',
    label: 'Identifies as male gender',
    display: 'Identifies as male gender (finding)',
    system: 'http://snomed.info/sct',
    fhirGender: 'male' as const,
  },
  {
    value: '33791000087105',
    label: 'Identifies as nonbinary gender',
    display: 'Identifies as nonbinary gender',
    system: 'http://snomed.info/sct',
    fhirGender: 'other' as const,
  },
  {
    value: 'UNK',
    label: 'Unknown',
    display: 'Unknown',
    system: 'http://terminology.hl7.org/CodeSystem/v3-NullFlavor',
    fhirGender: 'unknown' as const,
  },
  {
    value: 'asked-declined',
    label: 'Asked but declined',
    display: 'Asked But Declined',
    system: 'http://terminology.hl7.org/CodeSystem/data-absent-reason',
    fhirGender: 'unknown' as const,
  },
];

export const PRONOUNS_OPTIONS = [
  { value: 'LA29518-0', label: 'He/Him' },
  { value: 'LA29519-8', label: 'She/Her' },
  { value: 'LA29520-6', label: 'They/Them' },
];

// --- FHIR extension URLs ---

export const EXTENSION_URLS = {
  birthSex: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex',
  genderIdentity: 'http://hl7.org/fhir/us/core/StructureDefinition/us-core-genderIdentity',
  pronouns: 'http://hl7.org/fhir/StructureDefinition/individual-pronouns',
  coveragePhone: 'http://polarissleep.com/fhir/StructureDefinition/coverage-phone',
};

export const LOINC_SYSTEM = 'http://loinc.org';
export { ELIGIBILITY_CONFIRMATION_TAG } from '@polarissleep/shared';
